import React, { useState } from "react"
import { times } from "lodash"
import { PlayIcon } from '@heroicons/react/24/outline'

import Layout from "@/components/Layout"
import SEO from "@/components/SEO"
import { StaticImage } from "gatsby-plugin-image"
import WistiaPlayer from "@/components/WistiaPlayer"
import { graphql, Link } from "gatsby"
import { routes } from "@/utils/routes"
import Feature from "@/components/Feature"
import VideoModal from "@/components/VideoModal"

type PageProps = {
  data: any
}

export const pageQuery = graphql`
  query SchoolProjectsQuery {
    site {
      siteMetadata {
        title
      }
    }
  }
`

const SchoolProjects: React.FC<PageProps> = ({ data }: { data: any }) => {
  return (
    <Layout
      sectionOne={
        <>
          <div className="relative h-[67vh] md:min-h-[75vh]">
            <StaticImage
              src="../../static/images/transform-1.jpg"
              className="absolute inset-0 w-full h-full object-cover -z-50"
              alt="School Projects"
            />
            <div className="absolute inset-0 bg-[#131312]/50 -z-40"></div>

            <div className="container text-white h-[67vh] md:min-h-[75vh] flex items-center px-4">
              <div className="pl-2 md:pl-6 lg:pl-10 max-w-[1000px] translate-y-10 md:translate-y-16 lg:translate-y-20">
                <div className="pb-4">
                  <h1
                    className="text-left text-white text-[300%] sm:text-[400%] md:text-[531.25%] leading-[1] font-bold normal-case tracking-normal font-gilroy"
                  >
                    School Projects
                  </h1>
                  <div className="mt-5 h-[8px] sm:h-[10px] w-[200px] sm:w-[320px] bg-primary"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Gold four-option bar below the fold */}
          <div className="bg-primary w-full h-[33vh] md:h-auto md:block">
            <div className="flex flex-col h-full w-full md:grid md:grid-cols-4 md:gap-0 md:items-stretch md:min-h-[84px]">
              <a href={routes.socialMobility()} className="flex-1 flex items-center justify-center px-4 text-center uppercase font-gilroy font-bold text-[#131312] border-b border-white text-[19px] leading-[32.3px] md:border-b-0 md:border-r md:px-2">
                Transformative Bursaries
              </a>
              <a href={routes.southWingDevelopment()} className="flex-1 flex items-center justify-center px-4 text-center uppercase font-gilroy font-bold text-[#131312] border-b border-white text-[19px] leading-[32.3px] md:border-b-0 md:border-r md:px-2">
                The South Wing
              </a>
              <a href={routes.pledge()} className="flex-1 flex items-center justify-center px-4 text-center uppercase font-gilroy font-bold text-[#131312] border-b border-white text-[19px] leading-[32.3px] md:border-b-0 md:border-r md:px-2">
                Sponsor a Paviour
              </a>
              <a href={routes.donate()} className="flex-1 flex items-center justify-center px-4 text-center uppercase font-gilroy font-bold text-[#131312] text-[19px] leading-[32.3px] md:px-2">
                Donate
              </a>
            </div>
          </div>
        </>
      }
    >
      <SEO title="School Projects" />

      <div className="py-8 sm:py-14 px-4">
        <div className="container">
          <h2 className="mb-4 text-xl sm:text-2xl md:text-3xl uppercase font-gilroy font-bold">
            Help us create a <span className="text-primary">learning</span>{" "}
            environment which will{" "}
            <span className="text-primary">inspire our pupils</span>
          </h2>

          <WistiaPlayer
            videoID="d42s9ljqh5"
            videoStyle={{
              width: "100%",
              height: "100%",
            }}
            className="aspect-video"
          />

          <div className="flex justify-end mt-8 mb-8">
            <Link
              to={routes.donate()}
              className="inline-block bg-primary text-dark px-8 py-4 rounded-sm font-gilroy font-bold text-xl hover:bg-[#d9a01d] transition-colors text-center uppercase"
            >
              DONATE NOW
            </Link>
          </div>

          <p className="font-noto-serif text-[#6b6b6b] text-[100%] sm:text-[112.5%] leading-[1.6] font-light mt-4 text-center md:text-left">
            We are seeking:
          </p>

          <div className="grid gap-y-8 md:grid-cols-2 lg:grid-cols-4 pt-8">
            <Feature description="£10,000 to help fund state of the art cameras and screens to the Common Hall, enhancing school concerts, plays and weekly assembly talks." />
            <Feature description="£5,000 to upgrade our drama rehearsal studio and put momentum back into performing arts at RBAI, following the pandemic hiatus." />
            <Feature description="£2,500 to equip our Eco, Art and Robotics clubs as their memberships grow." />
            <Feature description="£1,500 to upgrade video analysis equipment, widening access to more hockey and rugby teams to benefit from video analysis." />
          </div>

        </div>
      </div>
    </Layout>
  )
}

export default SchoolProjects
